import styled from 'styled-components';
import PropTypes from 'prop-types';
import { values } from 'lodash';

import { COLORS } from '../../helpers/constants/styleguide';

import { FlexColumn } from '../Flex';
import Icon from '../Icon';

import { hexToRgba } from '../../helpers/utils/common';

const iconSizes = {
  DEFAULT: 'default',
  SMALL: 'small',
};

const paintOptions = {
  fill: 'fill',
  stroke: 'stroke',
  both: 'both',
};

/**
 * No rest spread: props not listed here are dropped rather than forwarded.
 *
 * @typedef {Object} IconMedallionProps
 * @property {React.ElementType} SvgComponent
 * @property {string} [className]
 * @property {string} [color] Values can be found in {@link COLORS}
 * @property {string | null} [iconColor] Values can be found in {@link COLORS}
 * @property {string} [backgroundColor] Values can be found in {@link COLORS}
 * @property {number} [size]
 * @property {'default' | 'small'} [iconSize]
 * @property {'fill' | 'stroke' | 'both'} [paintWith]
 */

/** @type {React.FC<IconMedallionProps>} */
const IconMedallion = ({
  // eslint-disable-next-line react/prop-types -- pre-existing; surfaced by reflow from a type-only change
  SvgComponent,
  // eslint-disable-next-line react/prop-types -- pre-existing; surfaced by reflow from a type-only change
  className,
  color = COLORS.azure,
  // eslint-disable-next-line react/prop-types -- pre-existing; surfaced by reflow from a type-only change
  iconColor = null,
  // eslint-disable-next-line react/prop-types -- pre-existing; surfaced by reflow from a type-only change
  backgroundColor = '',
  size = 40,
  iconSize = 'default',
  paintWith = 'fill',
}) => {
  const scale = iconSize === iconSizes.SMALL ? 0.36 : 0.6;

  const paintProps = {
    fill: paintWith !== 'stroke' ? (iconColor ?? color) : undefined,
    stroke: paintWith !== 'fill' ? (iconColor ?? color) : undefined,
  };

  return (
    <Circle
      color={color}
      backgroundColor={backgroundColor}
      size={size}
      className={className}
    >
      <Icon
        SvgComponent={SvgComponent}
        width={size * scale}
        height={size * scale}
        {...paintProps}
      />
    </Circle>
  );
};

const Circle = styled(FlexColumn).attrs({
  className: 'icon-medallion',
})`
  border-radius: 50%;
  background-color: ${props =>
    props.backgroundColor ?? hexToRgba(props.color, 0.2)};
  height: ${props => props.size}px;
  width: ${props => props.size}px;
  min-width: ${props => props.size}px;
  justify-content: center;
  align-items: center;
`;

IconMedallion.propTypes = {
  size: PropTypes.number,
  color: PropTypes.oneOf(values(COLORS)),
  iconSize: PropTypes.oneOf(values(iconSizes)),
  paintWith: PropTypes.oneOf(values(paintOptions)),
};

export default IconMedallion;
